import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import YearEndCloseWizard from '../components/YearEndCloseWizard';

/**
 * Fiscal Year Setup — admin / super-admin only.
 *
 * Phase 1: list FYs, create new ones, edit ranges/labels, delete unused ones.
 * Phase 2 will add the year-end closing wizard and opening-balance editor.
 */
const ymd = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const FiscalYears = () => {
  const { user } = useAuth();
  const { refresh: refreshGlobal } = useFiscalYear();
  const [items, setItems] = useState([]);
  const [currentFyId, setCurrentFyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', start_date: '', end_date: '' });
  const [error, setError] = useState('');
  const [closingFy, setClosingFy] = useState(null);
  const isAdmin = user && ['admin', 'super_admin'].includes(user.role);

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await api.get('/fiscal-years');
      setItems(res.data?.items || []);
      setCurrentFyId(res.data?.current_fy_id || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load fiscal years.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const onCreate = async () => {
    setError('');
    if (!form.name || !form.start_date || !form.end_date) {
      setError('Name, start date and end date are all required.');
      return;
    }
    try {
      setCreating(true);
      await api.post('/fiscal-years', form);
      setForm({ name: '', start_date: '', end_date: '' });
      await fetchList();
      await refreshGlobal();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to create fiscal year.');
    } finally {
      setCreating(false);
    }
  };

  const onStartEdit = (fy) => {
    setEditingId(fy.id);
    setEditForm({
      name: fy.name,
      start_date: fy.start_date,
      end_date: fy.end_date,
    });
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setError('');
  };

  const onSaveEdit = async (id) => {
    setError('');
    try {
      await api.put(`/fiscal-years/${id}`, editForm);
      setEditingId(null);
      await fetchList();
      await refreshGlobal();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save changes.');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this fiscal year? This cannot be undone.')) return;
    setError('');
    try {
      await api.delete(`/fiscal-years/${id}`);
      await fetchList();
      await refreshGlobal();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to delete fiscal year.');
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-4">
          Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto" data-testid="fiscal-years-page">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">📅 Fiscal Year Setup</h1>
      <p className="text-xs sm:text-sm text-gray-600 mb-5">
        Configure your fiscal years. The current year is auto-seeded on first
        use. Closing and opening-balance carry-forward come in Phase 2.
      </p>

      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4"
          data-testid="fy-error"
        >
          {error}
        </div>
      )}

      {/* Create new FY */}
      <div className="bg-white border rounded-lg p-4 mb-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">➕ Create Fiscal Year</h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Name</label>
            <input
              type="text"
              placeholder="FY 2027"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              className="w-full border rounded px-2 py-1.5 text-sm"
              data-testid="fy-create-name"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Start date</label>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
              className="w-full border rounded px-2 py-1.5 text-sm"
              data-testid="fy-create-start"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">End date</label>
            <input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
              className="w-full border rounded px-2 py-1.5 text-sm"
              data-testid="fy-create-end"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={onCreate}
              disabled={creating}
              className="w-full px-3 py-1.5 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
              data-testid="fy-create-btn"
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 text-sm font-semibold text-gray-700">
          All Fiscal Years
        </div>
        {loading ? (
          <div className="p-4 text-sm text-gray-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No fiscal years yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="fy-table">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Start</th>
                <th className="text-left px-3 py-2">End</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((fy) => {
                const isCurrent = fy.id === currentFyId;
                const isEditing = editingId === fy.id;
                return (
                  <tr key={fy.id} className="border-t" data-testid={`fy-row-${fy.id}`}>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        <span className="font-semibold">
                          {fy.name}
                          {isCurrent && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                              current
                            </span>
                          )}
                          {fy.auto_seeded && !isEditing && (
                            <span
                              className="ml-2 text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded"
                              title="Auto-created by the system on first use"
                            >
                              auto
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <input
                          type="date"
                          value={editForm.start_date}
                          onChange={(e) => setEditForm((p) => ({ ...p, start_date: e.target.value }))}
                          className="border rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        ymd(fy.start_date)
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <input
                          type="date"
                          value={editForm.end_date}
                          onChange={(e) => setEditForm((p) => ({ ...p, end_date: e.target.value }))}
                          className="border rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        ymd(fy.end_date)
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded ${
                          fy.status === 'closed'
                            ? 'bg-gray-200 text-gray-700'
                            : fy.status === 'closing'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {fy.status || 'open'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => onSaveEdit(fy.id)}
                            className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                            data-testid={`fy-save-${fy.id}`}
                          >
                            Save
                          </button>
                          <button
                            onClick={onCancelEdit}
                            className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setClosingFy(fy)}
                            className={`text-xs px-2 py-1 rounded border ${
                              fy.status === 'closed'
                                ? 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                                : fy.status === 'closing'
                                ? 'text-amber-700 border-amber-300 hover:bg-amber-50'
                                : 'text-blue-700 border-blue-300 hover:bg-blue-50'
                            }`}
                            data-testid={`fy-close-${fy.id}`}
                            title={
                              fy.status === 'closed'
                                ? 'View / reopen this closed year'
                                : fy.status === 'closing'
                                ? 'Resume editing opening balances'
                                : 'Preview & start the closing workflow'
                            }
                          >
                            {fy.status === 'closed'
                              ? '🔒 View'
                              : fy.status === 'closing'
                              ? '✏️ Resume'
                              : '🔐 Close'}
                          </button>
                          <button
                            onClick={() => onStartEdit(fy)}
                            className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                            data-testid={`fy-edit-${fy.id}`}
                            disabled={fy.status !== 'open'}
                            title={fy.status !== 'open' ? 'Reopen the year before editing' : ''}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => onDelete(fy.id)}
                            className="text-xs px-2 py-1 rounded border text-red-700 hover:bg-red-50 disabled:opacity-50"
                            data-testid={`fy-delete-${fy.id}`}
                            disabled={fy.auto_seeded || isCurrent || fy.status !== 'open'}
                            title={
                              fy.auto_seeded
                                ? 'Auto-seeded FY cannot be deleted'
                                : isCurrent
                                ? 'Cannot delete the FY containing today'
                                : ''
                            }
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-4">
        Tip: switch the active FY any time using the selector in the top header — the dashboard widgets and reports
        respect that selection automatically.
      </p>

      {closingFy && (
        <YearEndCloseWizard
          fy={closingFy}
          onClose={() => setClosingFy(null)}
          onChanged={async () => {
            await fetchList();
            await refreshGlobal();
            // Re-fetch the wizard's parent FY from the freshly-loaded list so
            // the wizard updates its status without remount.
            try {
              const res = await api.get('/fiscal-years');
              const updated = (res.data?.items || []).find((f) => f.id === closingFy.id);
              if (updated) setClosingFy(updated);
              else setClosingFy(null);
            } catch {
              setClosingFy(null);
            }
          }}
        />
      )}
    </div>
  );
};

export default FiscalYears;
